import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { message } from "@centrate-io/barn";
import {
  IconCreditCard,
  IconBuildingBank,
  IconX,
} from "@tabler/icons-react";
import { cpProcessPortalPayment } from "_graphql/mutations";
import {
  formatMoney,
  formatExpiration,
  isExpirationValid,
  validateRoutingNumber,
  validateAccountNumber,
} from "_assets/js/helpers";
import "./UpdatePaymentModal.scss";

const COUNTRY_OPTIONS = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "GB", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
];

function UpdatePaymentModal({ open, onClose, onSuccess, payment }) {
  const [activeTab, setActiveTab] = useState("credit");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Credit card fields
  const [ccNumber, setCcNumber] = useState("");
  const [ccExp, setCcExp] = useState("");
  const [ccCvc, setCcCvc] = useState("");
  const [ccName, setCcName] = useState("");
  const [country, setCountry] = useState("US");

  // Bank/ACH fields
  const [routingNumber, setRoutingNumber] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [confirmAccountNumber, setConfirmAccountNumber] = useState("");
  const [accountType, setAccountType] = useState("checking");

  const [processPayment] = useMutation(cpProcessPortalPayment);

  const token = localStorage.getItem("JWT");

  const resetForm = () => {
    setCcNumber("");
    setCcExp("");
    setCcCvc("");
    setCcName("");
    setCountry("US");
    setRoutingNumber("");
    setAccountNumber("");
    setConfirmAccountNumber("");
    setAccountType("checking");
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isCreditValid = () => {
    const cleanNumber = ccNumber.replace(/\s/g, "");
    return (
      cleanNumber.length >= 13 &&
      cleanNumber.length <= 19 &&
      isExpirationValid(ccExp) &&
      ccCvc.length >= 3 &&
      ccName.trim().length > 0
    );
  };

  const isBankValid = () => {
    return (
      validateRoutingNumber(routingNumber) &&
      validateAccountNumber(accountNumber) &&
      accountNumber === confirmAccountNumber
    );
  };

  const isValid = activeTab === "credit" ? isCreditValid() : isBankValid();

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 16);
    return cleaned.replace(/(.{4})/g, "$1 ").trim();
  };

  const handleSubmit = async () => {
    if (!isValid || loading) return;
    setLoading(true);
    setError(null);

    try {
      const variables = {
        token,
        payment_id: payment.id,
        payment_type: activeTab === "credit" ? "credit" : "ach",
      };

      if (activeTab === "credit") {
        variables.cc_name = ccName;
        variables.cc_number = ccNumber.replace(/\s/g, "");
        variables.cc_exp = ccExp;
        variables.cc_cvc = ccCvc;
      } else {
        variables.account_number = accountNumber;
        variables.routing_number = routingNumber;
        variables.account_type = accountType;
      }

      const result = await processPayment({ variables });
      const data = result?.data?.cpProcessPortalPayment;

      if (data?.success === "true") {
        message.success("Payment details have been updated!");
        resetForm();
        onSuccess();
      } else {
        setError(data?.error_message || "Payment processing failed. Please try again.");
      }
    } catch (err) {
      setError(err?.message || "An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const amount = payment?.amount || 0;

  return (
    <>
      <div className="update-payment-overlay" onClick={handleClose} />
      <div className="update-payment-modal">
        <div className="update-payment-modal__header">
          <h2 className="update-payment-modal__title">Update Payment Details</h2>
          <button className="update-payment-modal__close" onClick={handleClose}>
            <IconX size={20} />
          </button>
        </div>

        <div className="update-payment-modal__content">
          {/* Tabs */}
          <div className="update-payment-modal__tabs">
            <button
              className={`update-payment-modal__tab ${activeTab === "credit" ? "active" : ""}`}
              onClick={() => { setActiveTab("credit"); setError(null); }}
            >
              <IconCreditCard size={20} />
              <span>Credit / Debit</span>
            </button>
            <button
              className={`update-payment-modal__tab ${activeTab === "ach" ? "active" : ""}`}
              onClick={() => { setActiveTab("ach"); setError(null); }}
            >
              <IconBuildingBank size={20} />
              <span>Bank / ACH</span>
            </button>
          </div>

          {/* Credit Card Form */}
          {activeTab === "credit" && (
            <div className="update-payment-modal__form">
              <div className="update-payment-modal__field">
                <label>Card information</label>
                <input
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  value={ccNumber}
                  onChange={(e) => setCcNumber(formatCardNumber(e.target.value))}
                  maxLength={19}
                />
              </div>
              <div className="update-payment-modal__row">
                <input
                  type="text"
                  placeholder="MM/YY"
                  value={ccExp}
                  onChange={(e) => setCcExp(formatExpiration(e.target.value))}
                  maxLength={5}
                />
                <input
                  type="text"
                  placeholder="CVC"
                  value={ccCvc}
                  onChange={(e) => setCcCvc(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  maxLength={4}
                />
              </div>
              <div className="update-payment-modal__field">
                <label>Name on card</label>
                <input
                  type="text"
                  placeholder="Full name..."
                  value={ccName}
                  onChange={(e) => setCcName(e.target.value)}
                />
              </div>
              <div className="update-payment-modal__field">
                <label>Country or region</label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  {COUNTRY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Bank/ACH Form */}
          {activeTab === "ach" && (
            <div className="update-payment-modal__form">
              <div className="update-payment-modal__field">
                <label>Routing number</label>
                <input
                  type="text"
                  placeholder="9 digit routing number"
                  value={routingNumber}
                  onChange={(e) => setRoutingNumber(e.target.value.replace(/\D/g, "").slice(0, 9))}
                  maxLength={9}
                />
              </div>
              <div className="update-payment-modal__field">
                <label>Account number</label>
                <input
                  type="text"
                  placeholder="Account number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 16))}
                  maxLength={16}
                />
              </div>
              <div className="update-payment-modal__field">
                <label>Re-enter account number</label>
                <input
                  type="text"
                  placeholder="Re-enter account number"
                  value={confirmAccountNumber}
                  onChange={(e) => setConfirmAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 16))}
                  maxLength={16}
                />
                {confirmAccountNumber && accountNumber !== confirmAccountNumber && (
                  <span className="update-payment-modal__field-error">Account numbers do not match</span>
                )}
              </div>
              <div className="update-payment-modal__field">
                <label>Account type</label>
                <div className="update-payment-modal__radio-group">
                  <label className="update-payment-modal__radio">
                    <input
                      type="radio"
                      name="accountType"
                      value="checking"
                      checked={accountType === "checking"}
                      onChange={() => setAccountType("checking")}
                    />
                    <span>Checking</span>
                  </label>
                  <label className="update-payment-modal__radio">
                    <input
                      type="radio"
                      name="accountType"
                      value="savings"
                      checked={accountType === "savings"}
                      onChange={() => setAccountType("savings")}
                    />
                    <span>Savings</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {error && <div className="update-payment-modal__error">{error}</div>}

          <div className="update-payment-modal__actions">
            <button
              className="update-payment-modal__submit"
              disabled={!isValid || loading}
              onClick={handleSubmit}
            >
              {loading
                ? "Processing..."
                : activeTab === "credit"
                ? "Update Card Details"
                : "Update Bank Details"}
            </button>
            <p className="update-payment-modal__disclaimer">
              *A charge of {formatMoney(amount)} will be applied to your payment
              method. Your plan will automatically renew each month unless
              canceled.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default UpdatePaymentModal;
